/**
 * Import/validation mutation helpers used by the protocol detail hook.
 */

import { clinicalTrialsService } from "../../api/clinicalTrialsService";
import type {
  ProtocolImportPreview,
  ProtocolTrial,
  ProtocolValidationRun,
} from "../../api/clinicalTrialsApiTypes";

export type { ProtocolLifecycleAction } from "./protocolDetailTypes";
export {
  acceptSuggestion,
  addDraftConnection,
  archiveToggle,
  removeConnection,
  runLifecycle,
} from "./protocolDetailLifecycleActions";

type SetError = (value: string | null) => void;
type SetBusy = (value: boolean) => void;

/**
 * Preview an uploaded atoms CSV for a trial.
 *
 * @param trialId - Trial identifier
 * @param file - Selected CSV file
 * @param setBusy - Busy-state setter
 * @param setError - Error-state setter
 * @param setPreview - Preview-state setter
 * @returns Nothing
 */
export async function previewImport(
  trialId: string,
  file: File | null,
  setBusy: SetBusy,
  setError: SetError,
  setPreview: (value: ProtocolImportPreview | null) => void,
): Promise<void> {
  if (!file || !trialId) return;
  setBusy(true);
  setError(null);
  try {
    setPreview(await clinicalTrialsService.previewProtocolImport(trialId, file));
  } catch (err) {
    setError(err instanceof Error ? err.message : "Import preview failed");
  } finally {
    setBusy(false);
  }
}

/**
 * Confirm the current CSV preview into a draft.
 *
 * @param trialId - Trial identifier
 * @param preview - Current import preview
 * @param trial - Loaded trial metadata
 * @param setBusy - Busy-state setter
 * @param setError - Error-state setter
 * @param setPreview - Preview-state setter
 * @param loadTrial - Reload callback
 * @returns Nothing
 */
export async function confirmImport(
  trialId: string,
  preview: ProtocolImportPreview | null,
  trial: ProtocolTrial | null,
  setBusy: SetBusy,
  setError: SetError,
  setPreview: (value: ProtocolImportPreview | null) => void,
  loadTrial: () => Promise<void>,
): Promise<void> {
  if (!preview || !trialId) return;
  if (!window.confirm("Confirm CSV import into a new draft?")) return;
  setBusy(true);
  setError(null);
  try {
    await clinicalTrialsService.confirmProtocolImport(trialId, preview.job.import_id, {
      confirmation: true,
      replace_draft_id: trial?.latest_draft_id || undefined,
    });
    setPreview(null);
    await loadTrial();
  } catch (err) {
    setError(err instanceof Error ? err.message : "Import confirm failed");
  } finally {
    setBusy(false);
  }
}

/**
 * Validate the latest draft and refresh trial state.
 *
 * @param trialId - Trial identifier
 * @param trial - Loaded trial metadata
 * @param setBusy - Busy-state setter
 * @param setError - Error-state setter
 * @param setValidation - Validation-state setter
 * @param loadTrial - Reload callback
 * @returns Nothing
 */
export async function validateDraft(
  trialId: string,
  trial: ProtocolTrial | null,
  setBusy: SetBusy,
  setError: SetError,
  setValidation: (value: ProtocolValidationRun | null) => void,
  loadTrial: () => Promise<void>,
): Promise<void> {
  if (!trial?.latest_draft_id) return;
  setBusy(true);
  setError(null);
  try {
    setValidation(
      await clinicalTrialsService.validateProtocolDraft(trialId, trial.latest_draft_id),
    );
    await loadTrial();
  } catch (err) {
    setError(err instanceof Error ? err.message : "Validation failed");
  } finally {
    setBusy(false);
  }
}
